import { Link } from "react-router-dom";
import { Button } from "~/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { cn } from "~/lib/utils";
import { nextgenAdminEvalRunsPath } from "~/pages/manifold-nextgen/paths";
import type { EvalRunListItem } from "~/services/evals";
import { RunAgentAvatar } from "./primitives/RunAgentAvatar";
import { RunStatusBadge } from "./primitives/badges";
import { formatDuration, formatEvalTimestamp } from "./primitives/format";
import { parseApiTimestamp, runDurationSeconds } from "./selectors/core";
import type { RunAgentLabels } from "./useEvalApi";

const ROW_GRID_CLS = "grid grid-cols-4 items-center gap-4 px-6 py-3";

/** Rows the card holds, whether they are loading or loaded. */
const SKELETON_ROW_COUNT = 3;

const ColumnHeaders = () => (
  <div className={cn(ROW_GRID_CLS, "py-2 text-xs font-medium text-muted-foreground")}>
    <span>Started</span>
    <span>Status</span>
    <span>Agent</span>
    <span className="text-right">Duration</span>
  </div>
);

const RunRow = ({
  run,
  agentLabels,
  onSelectRun,
}: {
  run: EvalRunListItem;
  agentLabels: RunAgentLabels;
  onSelectRun: (runId: string) => void;
}) => (
  <button
    type="button"
    onClick={() => onSelectRun(run.id)}
    className={cn(
      ROW_GRID_CLS,
      "w-full cursor-pointer border-t border-border text-left text-sm transition-colors hover:bg-muted/40"
    )}
  >
    <span className="font-medium tabular-nums">
      {formatEvalTimestamp(parseApiTimestamp(run.started_at))}
    </span>
    <span>
      <RunStatusBadge status={run.status} />
    </span>
    <span className="flex min-w-0 items-center gap-2">
      {run.agent_id ? (
        <>
          <RunAgentAvatar agentId={run.agent_id} agentLabels={agentLabels} size={24} />
          <span
            className={cn(
              "truncate",
              !agentLabels.isNamed(run.agent_id) && "italic text-muted-foreground"
            )}
            title={run.agent_id}
          >
            {agentLabels.agentLabel(run.agent_id)}
          </span>
        </>
      ) : (
        <span className="text-muted-foreground">—</span>
      )}
    </span>
    {/* How long it took, which is the one fact about a run that is neither its
        identity nor its status. A run still going has not taken any yet. */}
    <span className="text-right text-xs tabular-nums text-muted-foreground">
      {formatDuration(runDurationSeconds(run))}
    </span>
  </button>
);

/** The newest runs, newest first. The runs page is where the rest are. */
export const RecentRunsCard = ({
  runs,
  agentLabels,
  isLoading,
  onSelectRun,
}: {
  runs: EvalRunListItem[];
  agentLabels: RunAgentLabels;
  isLoading: boolean;
  onSelectRun: (runId: string) => void;
}) => (
  <Card role="region" aria-label="Recent runs" className="gap-0 py-0">
    <CardHeader className="flex flex-row items-center justify-between gap-4 px-6 pt-5 pb-3">
      <div>
        <CardTitle className="text-base font-semibold">Recent runs</CardTitle>
        <p className="text-sm text-muted-foreground">Most recent first.</p>
      </div>
      <Button asChild variant="ghost" size="sm">
        <Link to={nextgenAdminEvalRunsPath()}>View all runs →</Link>
      </Button>
    </CardHeader>
    <CardContent className="p-0">
      {isLoading ? (
        <div role="status" aria-label="Loading runs">
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <div key={index} className={cn(ROW_GRID_CLS, "border-t border-border")}>
              <Skeleton className="h-4 w-28" />
              <Skeleton className="h-5 w-20" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="ml-auto h-4 w-10" />
            </div>
          ))}
        </div>
      ) : runs.length === 0 ? (
        <p className="border-t border-border px-6 py-8 text-center text-sm text-muted-foreground">
          No eval runs yet.
        </p>
      ) : (
        <>
          <ColumnHeaders />
          {runs.map((run) => (
            <RunRow key={run.id} run={run} agentLabels={agentLabels} onSelectRun={onSelectRun} />
          ))}
        </>
      )}
    </CardContent>
  </Card>
);
